import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Heart, Sparkles, Flame, BookOpen, Lock, Gift, Mic, Share2 } from "lucide-react";
import { safeStorage } from "@/lib/safeStorage";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ThreadSwitcher } from "@/components/ThreadSwitcher";
import { VoiceInput } from "@/components/VoiceInput";
import { SpeakButton } from "@/components/SpeakButton";
import ShareClipButton from "@/components/ShareClipButton";
import { AgeGate } from "@/components/AgeGate";
import { ModelPicker } from "@/components/ModelPicker";

const ACCENT = {
  amber: { text: "text-amber-300", ring: "ring-amber-400/40", bubble: "bg-amber-500/15 border-amber-500/30 text-amber-50", pill: "bg-amber-500/15 border-amber-500/40 text-amber-300" },
  emerald: { text: "text-emerald-300", ring: "ring-emerald-400/40", bubble: "bg-emerald-500/15 border-emerald-500/30 text-emerald-50", pill: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  rose: { text: "text-rose-300", ring: "ring-rose-400/40", bubble: "bg-rose-500/15 border-rose-500/30 text-rose-50", pill: "bg-rose-500/15 border-rose-500/40 text-rose-300" },
};

export default function CompanionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [c, setC] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [steamy, setSteamy] = useState({ active: false, cost_gems: 10, window_hours: 24 });
  const [continuing, setContinuing] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [model, setModel] = useState("claude");
  const [ageOpen, setAgeOpen] = useState(false);
  const [steamyPromptOpen, setSteamyPromptOpen] = useState(false);
  // Cinematic (ElevenLabs) voice toggle — persists per device.
  const [cinematic, setCinematic] = useState(() => safeStorage.getItem("ps_cinematic_voice") === "1");
  const [elevenReady, setElevenReady] = useState(false);
  const [elevenReason, setElevenReason] = useState(null);
  const scrollRef = useRef(null);
  const [access, setAccess] = useState({ locked: false, unlock_cost: 0 });
  const [guestLeft, setGuestLeft] = useState(5);
  const signupUrl = "/login?mode=signup&next=" + encodeURIComponent(window.location.pathname);
  const guestId = (() => {
    let g = safeStorage.getItem("ps_guest_id");
    if (!g) { g = `g_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; safeStorage.setItem("ps_guest_id", g); }
    return g;
  })();

  useEffect(() => {
    api.get("/voice/eleven/status").then((r) => {
      setElevenReady(!!r.data?.usable);
      setElevenReason(r.data?.reason || null);
    }).catch(() => setElevenReady(false));
  }, []);
  useEffect(() => {
    safeStorage.setItem("ps_cinematic_voice", cinematic ? "1" : "0");
  }, [cinematic]);

  useEffect(() => {
    api.get(`/companions/${id}`).then((r) => setC(r.data)).catch(() => setC(null));
    api.get(`/companions/${id}/access`, { params: { guest_id: guestId } }).then((r) => {
      setAccess(r.data);
      if (r.data.guest_remaining != null) setGuestLeft(r.data.guest_remaining);
    }).catch(() => {});
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || !id) return;
    api.get(`/companions/${id}/chat`).then((r) => {
      setMessages(r.data.messages || []);
      setThreadId(r.data.thread_id);
    }).catch(() => {});
    api.get(`/companions/${id}/steamy`).then((r) => setSteamy(r.data)).catch(() => {});
  }, [user, id]);
  // Reload messages when switching threads
  useEffect(() => {
    if (!user || !id || !threadId) return;
    api.get(`/companions/${id}/chat`, { params: { thread_id: threadId } })
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [threadId, user, id]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, busy]);

  if (!c) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Fetching them…</div>;

  const userMsgCount = messages.filter((m) => m.role === "user").length;
  const canContinueToStory = user && userMsgCount >= 4;
  const a = ACCENT[c.accent] || ACCENT.rose;
  const portrait = c.generated_portrait || c.image_url;
  const avatar = portrait && portrait.startsWith("/api/files/") ? `${portrait}?w=160` : portrait;

  const enableSteamy = async () => {
    // If not yet confirmed 18+, open age gate first.
    if (!user?.age_confirmed_18) {
      setAgeOpen(true);
      return;
    }
    try {
      const { data } = await api.post(`/companions/${id}/steamy`, { confirm: true });
      setSteamy({ ...steamy, active: true, until: data.until });
      toast.success(`Steamy Mode on until ${new Date(data.until).toLocaleTimeString()}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enable Steamy Mode");
    }
  };

  const turnIntoStory = async () => {
    setContinuing(true);
    try {
      const { data } = await api.post(`/companions/${id}/story`, {});
      toast.success("Your story is live");
      await refresh();
      nav(`/story/${data.story.story_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to spin story");
    } finally { setContinuing(false); }
  };

  const unlock = async () => {
    if (!user) { nav(signupUrl); return; }
    try {
      await api.post(`/companions/${id}/unlock`);
      setAccess({ ...access, locked: false });
      toast.success(`${c.name} unlocked — text as much as you like`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    }
  };

  const sendGuest = async (msg) => {
    const history = messages.slice(-8).map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: "user", text: msg, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/companions/${id}/guest-chat`, { guest_id: guestId, message: msg, history });
      setMessages((m) => [...m, { role: "companion", character: data.character, text: data.reply, created_at: new Date().toISOString() }]);
      setGuestLeft(data.remaining);
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      if (e.response?.status === 401) { setGuestLeft(0); }
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    if (!user && guestLeft <= 0) { nav(signupUrl); return; }
    const msg = text.trim();
    setText("");
    setBusy(true);
    if (!user) { await sendGuest(msg); setBusy(false); return; }
    setMessages((m) => [...m, { role: "user", text: msg, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/companions/${id}/chat`, { message: msg, thread_id: threadId, model });
      setMessages((m) => [...m, { role: "companion", character: data.character, text: data.reply, created_at: data.created_at }]);
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed";
      toast.error(detail);
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-6">
        <ArrowLeft className="h-3.5 w-3.5" /> All companions
      </Link>

      <div className="grid md:grid-cols-[minmax(0,360px)_1fr] gap-6 md:gap-10">
        {/* Left: portrait + bio */}
        <div>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <img src={c.generated_portrait || c.image_url} alt={c.name} className="w-full aspect-[3/4] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded border ${a.pill} mb-2`}>{c.trope}</span>
              <h1 data-testid="companion-name" className="font-serif text-3xl font-bold text-white leading-tight">{c.name}</h1>
              <p className="mt-1 text-slate-300 text-sm italic font-serif">"{c.tagline}"</p>
            </div>
          </div>
          <div className="mt-5 rounded-2xl border border-white/10 bg-[#0F1117] p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
              <Sparkles className={`h-3 w-3 ${a.text}`} /> Backstory
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{c.bio}</p>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Speaking style</div>
            <p className="text-xs text-slate-400 italic">{c.voice}</p>
          </div>
          {!user && (
            <Button onClick={() => nav(signupUrl)} data-testid="companion-signin" className="mt-4 w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
              Sign up free · unlimited chat with {c.name.split(' ')[0]}
            </Button>
          )}
        </div>

        {/* Right: live chat */}
        <div className="min-h-[70vh] flex flex-col rounded-3xl border border-white/10 bg-[#0F1117] overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-white/5 flex flex-col gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-10 w-10 rounded-full overflow-hidden ring-2 ${a.ring} shrink-0`}>
                <img src={avatar} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-white truncate">{c.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                  <Flame className={`h-3 w-3 ${a.text} shrink-0`} /> Online now · replies in seconds
                </div>
              </div>
              {steamy.active && (
                <span data-testid="steamy-active-badge" className="shrink-0 inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-500/15 text-rose-200 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest">
                  <Flame className="h-2.5 w-2.5" /> Steamy
                </span>
              )}
            </div>
            {/* Action row — scrolls horizontally on mobile, stays on one line on desktop */}
            <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 sm:flex-wrap sm:overflow-visible scrollbar-thin" data-testid="companion-header-actions">
              {elevenReady && (
                <button
                  data-testid="cinematic-voice-toggle"
                  onClick={() => {
                    setCinematic((v) => !v);
                    toast(cinematic ? "Studio voice off" : "Studio voice on", {
                      description: cinematic
                        ? "Replies read in default voice"
                        : `${c.name} will speak in a cinematic ElevenLabs voice`,
                    });
                  }}
                  title={cinematic ? "Studio voice is ON — tap to switch to default" : "Switch to ElevenLabs cinematic voice"}
                  className={`shrink-0 inline-flex items-center gap-1 rounded-full border h-8 px-3 text-[11px] font-mono uppercase tracking-widest transition-colors ${
                    cinematic
                      ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                      : "border-white/10 bg-white/5 text-slate-400 hover:text-amber-300 hover:border-amber-400/40"
                  }`}
                >
                  <Mic className="h-3 w-3" /> Studio{cinematic ? " · on" : ""}
                </button>
              )}
              {user && threadId && (
                <button
                  data-testid="share-thread-button"
                  onClick={async () => {
                    const url = `${window.location.origin}/replay/${threadId}`;
                    try {
                      if (navigator.share) {
                        await navigator.share({ title: `${c.name} · ProseCraft mini-fic`, url });
                        return;
                      }
                    } catch (_) {}
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success("Share link copied — paste it anywhere");
                    } catch {
                    toast.error("Could not copy — long-press this link instead");
                  }
                }}
                title="Share this chat as a mini-fic card"
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 hover:border-amber-400/50 hover:text-amber-200 text-slate-400 h-8 px-3 text-[11px] font-mono uppercase tracking-widest"
              >
                <Share2 className="h-3 w-3" /> Share thread
              </button>
            )}
            {user && !steamy.active && !access.locked && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="steamy-toggle" className="shrink-0 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 text-rose-200 h-8 px-3 text-[11px] font-mono uppercase tracking-widest">
                    <Flame className="h-3 w-3" /> Steamy · {steamy.cost_gems}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0F1117] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif text-2xl flex items-center gap-2"><Flame className="h-5 w-5 text-rose-400" /> Enable Steamy Mode?</AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-slate-400">
                      18+. Charges <span className="text-rose-300 font-semibold">{steamy.cost_gems} gems</span> for a {steamy.window_hours}-hour window with {c.name}. Replies lean into sensual tension and longing — tasteful, never graphic. Content involving minors is strictly prohibited and will lock Steamy on your account.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-full">Not yet</AlertDialogCancel>
                    <AlertDialogAction data-testid="steamy-confirm" onClick={enableSteamy} className="rounded-full bg-rose-500 hover:bg-rose-400 text-white">
                      Yes, {steamy.cost_gems} gems
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {user && (
              <AgeGate
                open={ageOpen}
                onOpenChange={setAgeOpen}
                onConfirmed={async () => {
                  // after age confirmed, actually charge + enable steamy
                  try {
                    const { data } = await api.post(`/companions/${id}/steamy`, { confirm: true });
                    setSteamy({ ...steamy, active: true, until: data.until });
                    toast.success(`Steamy Mode on until ${new Date(data.until).toLocaleTimeString()}`);
                    await refresh();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to enable Steamy Mode");
                  }
                }}
              />
            )}
            {user && !access.locked && (
              <>
                <Link
                  data-testid="gift-link"
                  to={`/gift/create?companion=${id}`}
                  className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 text-amber-200 h-8 px-3 text-[11px] font-mono uppercase tracking-widest"
                >
                  <Gift className="h-3 w-3" /> Gift
                </Link>
                <div className="shrink-0">
                  <ThreadSwitcher
                    companionId={id}
                    activeThreadId={threadId}
                    onPick={(tid) => setThreadId(tid)}
                  />
                </div>
              </>
            )}
            </div>
          </div>
          <div ref={scrollRef} data-testid="companion-chat-scroll" className="flex-1 overflow-y-auto p-5 space-y-3">
            {(
              <div className="max-w-md">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{c.name} says</div>
                <div className={`rounded-2xl rounded-bl-md px-4 py-3 border ${a.bubble} font-serif italic text-sm leading-relaxed`}>
                  {c.opening_line}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[80%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm bg-white/8 border border-white/12 text-slate-100">{m.text}</div>
                </div>
              ) : (
                <div key={i} className="flex justify-start gap-2">
                  <div className={`h-8 w-8 rounded-full overflow-hidden ring-2 ${a.ring} flex-shrink-0`}>
                    <img src={avatar} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-col items-start gap-1 max-w-[75%]">
                    <div className={`rounded-2xl rounded-bl-md px-4 py-2.5 border ${a.bubble} font-serif italic text-sm leading-relaxed`}>{m.text}</div>
                    {user && (
                      <div className="flex items-center gap-1.5">
                        <SpeakButton text={m.text} companionId={id} engine={cinematic ? "eleven" : undefined} size="xs" />
                        <ShareClipButton text={m.text} companionId={id} />
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
            {busy && (
              <div className="flex justify-start gap-2">
                <div className={`h-8 w-8 rounded-full overflow-hidden ring-2 ${a.ring}`}><img src={avatar} alt="" className="h-full w-full object-cover" /></div>
                <div className="rounded-2xl px-4 py-2.5 bg-white/5 border border-white/10">
                  <span className="inline-flex gap-1">
                    <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${a.text}`.replace("text-", "bg-")} />
                    <span className={`h-1.5 w-1.5 rounded-full animate-pulse`} style={{background: 'rgba(244,63,94,0.8)', animationDelay: "120ms"}} />
                    <span className={`h-1.5 w-1.5 rounded-full animate-pulse`} style={{background: 'rgba(245,158,11,0.9)', animationDelay: "240ms"}} />
                  </span>
                </div>
              </div>
            )}
          </div>
          {canContinueToStory && (
            <div data-testid="continue-to-story-bar" className="px-4 py-3 border-t border-white/5 bg-gradient-to-r from-amber-500/10 to-rose-500/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-amber-100 min-w-0">
                <BookOpen className="h-4 w-4 text-amber-400 flex-shrink-0" />
                <span className="truncate">Spin this chat into a 4-chapter story with its own cover.</span>
              </div>
              <Button
                data-testid="turn-into-story"
                size="sm"
                onClick={turnIntoStory}
                disabled={continuing}
                className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs h-8 px-4 whitespace-nowrap"
              >
                {continuing ? "Writing…" : "Turn into story · 30 gems"}
              </Button>
            </div>
          )}
          {access.locked ? (
            <div data-testid="companion-locked" className="p-5 border-t border-white/5 bg-gradient-to-r from-amber-500/10 to-sky-500/10 flex flex-col sm:flex-row items-center gap-3 justify-between">
              <div className="flex items-center gap-2 text-sm text-amber-100"><Lock className="h-4 w-4 text-amber-400" /> {c.name.split(' ')[0]} is a {access.rarity} character.</div>
              <div className="flex gap-2">
                <Button data-testid="companion-unlock" onClick={unlock} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-10 px-5">Unlock · {access.unlock_cost} gems</Button>
                <Link to="/pricing" data-testid="companion-locked-subscribe" className="rounded-full border border-white/20 text-slate-200 h-10 px-5 inline-flex items-center text-sm">Subscribe</Link>
              </div>
            </div>
          ) : !user && guestLeft <= 0 ? (
            <div data-testid="guest-limit-reached" className="p-5 border-t border-white/5 bg-gradient-to-r from-amber-500/10 to-sky-500/10 flex flex-col sm:flex-row items-center gap-3 justify-between">
              <div className="text-sm text-amber-100">{c.name.split(' ')[0]} wants to keep talking. Free account = unlimited messages.</div>
              <Button data-testid="guest-signup" onClick={() => nav(signupUrl)} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-10 px-5">Sign up free</Button>
            </div>
          ) : (
          <form onSubmit={send} className="p-4 border-t border-white/5 flex items-center gap-2">
            {user && (
              <VoiceInput
                disabled={busy}
                onTranscript={(t) => setText((prev) => (prev ? `${prev} ${t}` : t))}
              />
            )}
            <Input
              data-testid="companion-chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Reply to ${c.name.split(' ')[0]}…`}
              disabled={busy}
              className="bg-[#07080A] border-white/10 h-11"
            />
            <Button data-testid="companion-chat-send" type="submit" disabled={busy || !text.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black h-11 w-11 p-0">
              <Send className="h-4 w-4" />
            </Button>
          </form>
          )}
          {user && (
            <div className="px-4 pb-2 flex items-center justify-between gap-2">
              <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest">Model</span>
              <ModelPicker value={model} onChange={setModel} />
            </div>
          )}
          <div data-testid="companion-chat-pricing-note" className="px-4 pb-3 text-[11px] text-slate-500 font-mono uppercase tracking-widest flex items-center gap-1">
            <Heart className="h-3 w-3 text-rose-400" /> {user ? "Unlimited free texting · voice, steamy & clips are extras" : `Guest preview · ${guestLeft} free messages left · sign up free for unlimited`}
          </div>
        </div>
      </div>
    </div>
  );
}
